import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraOverlayDrawer, SpecraRecordRail, SpecraTimeline } from '@mono/ui-components/captains';
import { SpecraBadge, SpecraButton, SpecraHeading, SpecraLink, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraInlineBanner, SpecraKeyValueRow, SpecraRestrictedChip, SpecraUnverifiedClaim } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { rateTypeVariant } from '../../../../pages/Registries/badgeVariants';
import { basisLabel, dateLabel, linkLabel, money, pricingLinkVariant } from './ratesView';
const T = Testids.pricingWorkspace;
export function RateRowRail({ detail, canEdit, access, onSetRate, onDecide, vehicleName }) {
    const [history, setHistory] = useState(false);
    const row = detail.row;
    if (!row) return null;
    const link = row.link;
    const writer = canEdit && !row.removedFromSectionB;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraRecordRail, {
                "data-testid": T.rail,
                sections: [
                    {
                        id: 'source',
                        title: 'Where this row comes from',
                        children: /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(SpecraHeading, {
                                    size: "sm",
                                    children: row.title
                                }),
                                /*#__PURE__*/ _jsxs(SpecraText, {
                                    size: "meta",
                                    tone: "muted",
                                    children: [
                                        "Matched to",
                                        ' ',
                                        link?.entryHref ? /*#__PURE__*/ _jsx(SpecraLink, {
                                            href: link.entryHref,
                                            children: link.entryTitle
                                        }) : 'Not matched'
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: pricingLinkVariant(link),
                                    children: linkLabel(link)
                                }),
                                link?.needsDecision && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                                    tone: "warning",
                                    children: [
                                        "The RFP title matches a ",
                                        link.entryVehicleName || 'different vehicle',
                                        " labor category, not one on ",
                                        vehicleName || 'the proposal vehicle',
                                        ". Decide how to price it.",
                                        writer && /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "xs",
                                            variant: "secondary",
                                            "data-testid": T.mapping,
                                            onClick: onDecide,
                                            children: "Decide"
                                        })
                                    ]
                                }),
                                link?.decision && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                                    tone: "neutral",
                                    children: [
                                        linkLabel(link),
                                        ": ",
                                        link.decisionReason,
                                        " · ",
                                        link.decidedByName,
                                        ",",
                                        ' ',
                                        dateLabel(link.decidedAt),
                                        writer && /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "xs",
                                            variant: "secondary",
                                            onClick: onDecide,
                                            children: "Change mapping"
                                        })
                                    ]
                                })
                            ]
                        })
                    },
                    {
                        id: 'observations',
                        title: 'Every observation',
                        children: /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                !access ? /*#__PURE__*/ _jsx(SpecraRestrictedChip, {
                                    label: "Withheld",
                                    reason: "ratebook.columns.read access needed"
                                }) : detail.observations.length ? detail.observations.map((o)=>/*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                        "data-testid": T.railObservation,
                                        variant: "fact",
                                        dense: true,
                                        label: /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx(SpecraBadge, {
                                                    variant: rateTypeVariant(o.observationType),
                                                    children: o.typeLabel
                                                }),
                                                /*#__PURE__*/ _jsxs(SpecraText, {
                                                    size: "meta",
                                                    tone: "muted",
                                                    children: [
                                                        o.companyName,
                                                        " · ",
                                                        o.sourceLabel,
                                                        o.crossVehicle ? ' · other vehicle' : ''
                                                    ]
                                                }),
                                                o.observationType === 'INFERRED' && /*#__PURE__*/ _jsx(SpecraUnverifiedClaim, {
                                                    compact: true,
                                                    kindLabel: "Estimate",
                                                    claim: "Evidence only"
                                                })
                                            ]
                                        }),
                                        value: `${money(o.amount)}${o.rateUnit && o.rateUnit !== 'HOURLY' ? `/${o.rateUnit}` : ''}`
                                    }, `${o.observationId}:${o.version}`)) : /*#__PURE__*/ _jsx(SpecraText, {
                                    children: "No observations on this labor category yet"
                                }),
                                writer && access && /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "secondary",
                                    onClick: ()=>onSetRate('observation'),
                                    children: "Pin an observation"
                                })
                            ]
                        })
                    },
                    {
                        id: 'why',
                        title: 'Why this suggestion',
                        children: /*#__PURE__*/ _jsxs("div", {
                            "data-testid": T.railWhy,
                            children: [
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "p",
                                    size: "body-sm",
                                    children: row.suggested?.explanation || 'No suggestion available'
                                }),
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    size: "meta",
                                    tone: "muted",
                                    children: row.suggested?.ruleLabel
                                }),
                                row.suggested?.inputs.map((i)=>/*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                        dense: true,
                                        label: /*#__PURE__*/ _jsxs(SpecraText, {
                                            tone: i.excludeReason ? 'muted' : undefined,
                                            children: [
                                                i.observationType,
                                                " · ",
                                                i.companyName,
                                                i.excludeReason ? ` · ${i.excludeReason}` : ''
                                            ]
                                        }),
                                        value: /*#__PURE__*/ _jsx(SpecraText, {
                                            tone: i.excludeReason ? 'muted' : undefined,
                                            children: money(i.amount)
                                        })
                                    }, `${i.observationId}:${i.version}`))
                            ]
                        })
                    },
                    ...row.adjustment ? [
                        {
                            id: 'adjustment',
                            title: 'Suggested → Using',
                            children: /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                        dense: true,
                                        variant: "fact",
                                        label: "Suggested",
                                        value: /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                money(row.suggested?.amount),
                                                /*#__PURE__*/ _jsx(SpecraText, {
                                                    size: "meta",
                                                    tone: "muted",
                                                    children: row.suggested?.ruleLabel
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                        dense: true,
                                        variant: "fact",
                                        label: /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx(SpecraText, {
                                                    children: "Team adjustment"
                                                }),
                                                /*#__PURE__*/ _jsxs(SpecraBadge, {
                                                    variant: "info",
                                                    children: [
                                                        row.adjustment.pct,
                                                        "%"
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs(SpecraText, {
                                                    size: "meta",
                                                    tone: "muted",
                                                    children: [
                                                        row.adjustment.scopeLabel,
                                                        " · ",
                                                        row.adjustment.reason,
                                                        " ·",
                                                        ' ',
                                                        row.adjustment.setByName,
                                                        ", ",
                                                        dateLabel(row.adjustment.setAt)
                                                    ]
                                                })
                                            ]
                                        }),
                                        value: row.adjustedAmount ? money(row.adjustedAmount) : '—, ceilings are not adjusted'
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                        dense: true,
                                        variant: "fact",
                                        label: "Using",
                                        value: /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                money(row.using?.amount),
                                                /*#__PURE__*/ _jsx(SpecraText, {
                                                    size: "meta",
                                                    children: row.using?.reason
                                                })
                                            ]
                                        })
                                    })
                                ]
                            })
                        }
                    ] : [],
                    {
                        id: 'using',
                        title: 'Using · audited reason',
                        children: /*#__PURE__*/ _jsxs("div", {
                            "data-testid": T.railUsing,
                            children: [
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "p",
                                    children: row.using?.reason || 'No Using value yet: a person must choose.'
                                }),
                                /*#__PURE__*/ _jsxs(SpecraText, {
                                    size: "meta",
                                    tone: "muted",
                                    children: [
                                        basisLabel(row),
                                        " · ",
                                        row.using?.setByName,
                                        " · ",
                                        row.using?.setAt
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(SpecraButton, {
                                    variant: "link",
                                    onClick: ()=>setHistory(true),
                                    children: [
                                        "History (",
                                        detail.history.length,
                                        ")"
                                    ]
                                }),
                                writer && /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "xs",
                                    variant: "secondary",
                                    onClick: ()=>onSetRate(),
                                    children: "Change"
                                })
                            ]
                        })
                    },
                    {
                        id: 'access',
                        title: 'Access',
                        children: /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsxs(SpecraText, {
                                    as: "p",
                                    size: "body-sm",
                                    children: [
                                        "Pricing and Growth can edit rates. What Pricing sees of the pursuit and black hat is set by your organisation's access rules (default: what the pursuit carried in, no black hat). One rule, enforced in zou, Cerbos, ryugu and the document server.",
                                        !canEdit && ' You can view this row.'
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                    label: "Person-level rates",
                                    value: /*#__PURE__*/ _jsx(SpecraRestrictedChip, {
                                        "data-testid": T.vaultChip,
                                        label: "Comp vault",
                                        reason: "Person-level pay is visible only to explicit comp-vault grantees, per legal entity. Every read is logged."
                                    })
                                })
                            ]
                        })
                    }
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
                open: history,
                onOpenChange: setHistory,
                title: "Rate history",
                children: /*#__PURE__*/ _jsx(SpecraTimeline, {
                    variant: "dated",
                    emptyText: "No rate changes yet",
                    items: detail.history.map((h, i)=>({
                            id: String(i),
                            title: h.summary,
                            date: dateLabel(h.createdAt),
                            description: /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        size: "meta",
                                        tone: "muted",
                                        children: h.actorName
                                    }),
                                    h.reason
                                ]
                            })
                        }))
                })
            })
        ]
    });
}
